import React, {
  FunctionComponent,
  useCallback,
  useMemo,
} from 'react';
import {
  useFieldArray,
  UseFieldArrayMove,
  UseFieldArrayRemove,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { Virtuoso } from 'react-virtuoso';
import { v4 as uuidv4 } from 'uuid';
import { FaColumns } from 'react-icons/fa';

import { cn } from '../../../../../../utilities/cn';

import type { KidDocumentForm, PdfElement, LayoutConfig, PdfTag, ChartTag } from '../../types/KidPdfGeneratorV2.types';
import BlockInputWrapper from '../wrapper/BlockInputWrapper';
import TextInputTipTap from './TextInputTipTap';
import SRIInput from './SRIInput';
import AddBlockChooser from '../AddBlockChooser';
import {
  groupElementsByLayout,
  getLayoutPresetName,
} from '../../../kids/utils/editor.utils';
import { KidMarker } from '../../../../../../api/kids/types';
import TableInputSimple from './TableInputComponent';
import { createDefaultTableContent } from '../../utils/kidDataTransformers';

export const isChartTag = (tag: PdfTag): tag is ChartTag => {
  return (
    tag === 'sri_chart' ||
    tag === 'bar_chart' ||
    tag === 'line_chart' ||
    tag === 'pie_chart' ||
    tag === 'benchmark_chart'
  );
};

interface SideBarEditorProps {
  data?: any;
  /** When provided, rendered as an absolute overlay on top of the sidebar (e.g. "Ready for Review" state) */
  readOnlyOverlay?: React.ReactNode;
  markers: KidMarker[];
  /** Template ids that are invalid/inconsistent for this document (should be removed). */
  invalidTemplateIds?: Set<string>;
}


// Component that watches the tag and conditionally renders the child
const FieldRenderer: FunctionComponent<{
  field: PdfElement;
  index: number;
  fieldsLength: number;
  remove: UseFieldArrayRemove;
  move: UseFieldArrayMove;
  addField: (type: PdfTag, index: number, layout?: LayoutConfig) => number;
  register: ReturnType<typeof useFormContext<KidDocumentForm>>['register'];
  markers: KidMarker[];
  isComplete?: boolean;
  invalidTemplateIds?: Set<string>;
}> = ({
  field,
  index,
  fieldsLength,
  remove,
  move,
  addField,
  register,
  markers,
  isComplete = false,
  invalidTemplateIds,
}) => {
    const { control } = useFormContext<KidDocumentForm>();
    const templateId = useWatch({
      name: `elements.${index}.template_id`,
      control,
    }) as string | undefined;

    // Watch the tag for this field to ensure re-render when tag changes
    const currentTag = useWatch({
      name: `elements.${index}.tag`,
      control,
    }) as PdfTag | undefined;

    // Use the watched tag or fallback to field.tag
    const tag = currentTag || field.tag;

    // Render the content
    const renderContent = () => {
      if (tag === 'text') {
        return (
          <BlockInputWrapper
            key={`${field.id}-${tag}`}
            fieldsLength={fieldsLength}
            remove={remove}
            move={move}
            index={index}
            addField={addField}
            isComplete={isComplete}
            invalidTemplateIds={invalidTemplateIds}
          >
            <TextInputTipTap
              key={`${field.id}-${index}-${tag}`}
              field={field as unknown as PdfElement}
              index={index}
              isReadOnly={Boolean(templateId)}
            />
          </BlockInputWrapper>
        );
      } else if (tag === 'heading') {
        return (
          <BlockInputWrapper
            key={`${field.id}-${tag}`}
            fieldsLength={fieldsLength}
            remove={remove}
            move={move}
            index={index}
            addField={addField}
            isComplete={isComplete}
            invalidTemplateIds={invalidTemplateIds}
          >
            <TextInputTipTap
              key={`${field.id}-${index}-${tag}`}
              field={field as unknown as PdfElement}
              index={index}
              allowStyling={false}
              customClassName="outline-none p-2 text-2xl font-semibold text-gray-900"
              isReadOnly={Boolean(templateId)}
            />
          </BlockInputWrapper>
        );
      } else if (tag === 'table' || tag === 'costs_over_time_table') {
        return (
          <BlockInputWrapper
            key={`${field.id}-${tag}`}
            fieldsLength={fieldsLength}
            remove={remove}
            move={move}
            index={index}
            addField={addField}
            isComplete={isComplete}
            invalidTemplateIds={invalidTemplateIds}
          >
            <TableInputSimple
              field={field as unknown as PdfElement}
              index={index}
              markers={markers}
            />
          </BlockInputWrapper>
        );
      } else if (tag === 'sri_chart') {
        return (
          <BlockInputWrapper
            key={`${field.id}-${tag}`}
            fieldsLength={fieldsLength}
            remove={remove}
            move={move}
            index={index}
            addField={addField}
            isComplete={isComplete}
            invalidTemplateIds={invalidTemplateIds}
          >
            <SRIInput
              key={`${field.id}-${tag}`}
              field={field as unknown as PdfElement}
              index={index}
            />
          </BlockInputWrapper>
        );
      } else if (isChartTag(tag as ChartTag)) {
        return (
          <BlockInputWrapper
            key={`${field.id}-${tag}`}
            fieldsLength={fieldsLength}
            remove={remove}
            move={move}
            index={index}
            addField={addField}
            isComplete={isComplete}
            invalidTemplateIds={invalidTemplateIds}
          >
            <h1>I am chart {tag}</h1>
          </BlockInputWrapper>
        );
      } else if (tag === 'divider') {
        return (
          <BlockInputWrapper
            key={`${field.id}-${tag}`}
            fieldsLength={fieldsLength}
            remove={remove}
            move={move}
            index={index}
            addField={addField}
            isComplete={isComplete}
            invalidTemplateIds={invalidTemplateIds}
          >
            <div className="my-4 flex items-center">
              <div className="flex-1 border-t border-gray-300"></div>
            </div>
          </BlockInputWrapper>
        );
      } else if (tag === 'page_break') {
        return (
          <BlockInputWrapper
            key={`${field.id}-${tag}`}
            fieldsLength={fieldsLength}
            remove={remove}
            move={move}
            index={index}
            addField={addField}
            isComplete={isComplete}
            invalidTemplateIds={invalidTemplateIds}
          >
            <div className="my-6 flex items-center justify-center">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <div className="h-px w-8 bg-gray-300"></div>
                <span className="font-medium">Page Break</span>
                <div className="h-px w-8 bg-gray-300"></div>
              </div>
            </div>
          </BlockInputWrapper>
        );
      }
      return null;
    };

    return <div>{renderContent()}</div>;
  };



/* -------------------------------------------------------------------------- */
/*                         Performance Test Renderers                         */
/* -------------------------------------------------------------------------- */

interface ElementsRendererProps {
  fields: Array<{ id: string } & Partial<PdfElement>>;
  remove: UseFieldArrayRemove;
  move: UseFieldArrayMove;
  addField: (type: PdfTag, index: number, layout?: LayoutConfig) => number;
  register: ReturnType<typeof useFormContext<KidDocumentForm>>['register'];
  markers: KidMarker[];
  invalidTemplateIds?: Set<string>;
}

const VirtuosoScroller = React.forwardRef(function VirtuosoScroller(props: any, ref: any) {
  return <div ref={ref} {...props} tabIndex={-1} />;
});

const VirtuosoList = React.forwardRef(function VirtuosoList(props: any, ref: any) {
  return <div ref={ref} {...props} className={cn(props.className, 'px-6 pb-6')} />;
});

// Grouped renderer with layout logic (original implementation)
// This is where the expensive watch/merge happens - only when using grouped renderer
const GroupedElementsRenderer: FunctionComponent<ElementsRendererProps> = ({
  fields,
  remove,
  move,
  addField,
  register,
  markers,
  invalidTemplateIds,
}) => {
  const { control, getValues } = useFormContext<KidDocumentForm>();

  // This is a fucked way of ensuring elements are consistant - I will figure a more polished solution later
  // RHF is super optimised renders and this is a hack to ensure elements are consistant for the UI on layout config
  //groupings

  // This is affecting the colour picker and causing extreme lag when removing elements etc
  // Only needed for grouped renderer to sync watched properties
  const watchedElements = useWatch({
    control,
    name: 'elements',
  }) as PdfElement[] | undefined;

  // Use fields as the source of truth for structure, only merge watched properties when they exist
  // This ensures removals are immediately reflected in the UI
  // Only merge if array lengths match to prevent stale data after removals
  const elements = fields.map((field, index) => {
    // While useWatch catches up after insert/remove, read the live form value.
    if (watchedElements && watchedElements.length !== fields.length) {
      const fromForm = getValues(`elements.${index}`);
      if (fromForm) {
        return { ...(field as unknown as PdfElement), ...fromForm, id: field.id };
      }
      return field as unknown as PdfElement;
    }

    const watchedElement = watchedElements?.[index];

    // Only merge if watchedElement exists and matches the field by id/fieldId
    // This prevents stale data from causing issues after removals
    if (
      watchedElement &&
      'tag' in watchedElement &&
      'fieldId' in watchedElement &&
      (watchedElement.fieldId === field.id ||
        watchedElement.fieldId === (field as any).fieldId)
    ) {
      return { ...(field as unknown as PdfElement), ...watchedElement };
    }

    return field as unknown as PdfElement;
  });

  const groups = groupElementsByLayout(elements);

  // Flatten groups into virtualizable items
  // Each item can be: a standalone element, or a group (header + elements + button)
  type VirtualItem =
    | { type: 'add-block-top' }
    | { type: 'element'; field: PdfElement; index: number }
    | { type: 'group'; groupIdx: number; group: (typeof groups)[0] };

  const virtualItems = useMemo(() => {
    const items: VirtualItem[] = [{ type: 'add-block-top' }];
    groups.forEach((group, groupIdx) => {
      const expectedCount = group.layout?.length || 0;
      const hasLayout = group.layout && group.layout.length > 0;

      if (!hasLayout || expectedCount <= 1) {
        group.elements.forEach(({ field, index }) => {
          items.push({ type: 'element', field, index });
        });
      } else {
        items.push({ type: 'group', groupIdx, group });
      }
    });
    return items;
  }, [groups]);

  return (
    <Virtuoso
      style={{ height: '100%' }}
      totalCount={virtualItems.length}
      components={{
        Scroller: VirtuosoScroller as any,
        List: VirtuosoList as any,
      }}
      itemContent={(index) => {
        const item = virtualItems[index];

        if (item.type === 'add-block-top') {
          return (
            <div key="add-block-top" className="pt-6 pb-4">
              <AddBlockChooser onChoose={(type) => addField(type, 0)} />
            </div>
          );
        }

        if (item.type === 'element') {
          return (
            <div key={`element-${item.field.id}-${index}`} className="pb-4">
              <FieldRenderer
                field={item.field}
                index={item.index}
                fieldsLength={elements.length}
                remove={remove}
                move={move}
                addField={addField}
                register={register}
                markers={markers}
                invalidTemplateIds={invalidTemplateIds}
              />
            </div>
          );
        }

        // Render group
        const { group } = item;
        const expectedCount = group.layout?.length || 0;
        const actualCount = group.elements.length;
        const isComplete = expectedCount > 0 && expectedCount === actualCount;

        return (
          <div key={`group-${item.groupIdx}-${index}`} className="pb-4">
            <div
              className={`relative my-2 rounded-xl border p-1 transition-all ${isComplete
                ? 'border-blue-400 bg-blue-50/30'
                : 'border-orange-300 bg-orange-50/30'
                }`}
            >
              {/* Group header */}
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 rounded-md border border-gray-400 bg-white px-2 py-1 text-xs font-medium shadow-sm">
                    <FaColumns className="text-blue-500" />
                    <span className="text-gray-700">
                      {getLayoutPresetName(group.layout)}
                    </span>
                    <span className="text-gray-400">
                      ({group.layout?.join(':')})
                    </span>
                  </div>
                  {!isComplete && (
                    <span className="rounded-md bg-orange-100 px-2 py-1 text-xs font-medium text-orange-700">
                      {actualCount} of {expectedCount} elements
                    </span>
                  )}
                  {isComplete && (
                    <span className="rounded-md border border-green-600 bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                      ✓ Complete
                    </span>
                  )}
                </div>
              </div>

              <div className="relative">
                <div className="absolute bottom-0 left-3 top-0 w-px bg-gray-300" />

                <div className="space-y-3">
                  {group.elements.map(
                    ({ field, index: elementIndex }, elementIdx) => (
                      <div
                        key={`group-${item.groupIdx}-element-${field.id}-${elementIdx}`}
                        className="flex items-start gap-2"
                      >
                        {/* Column number indicator */}
                        <div className="relative z-10 mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded border border-indigo-300 bg-indigo-50 text-xs font-medium text-indigo-700">
                          {elementIdx + 1}
                        </div>
                        {/* Element */}
                        <div className="flex-1">
                          <FieldRenderer
                            field={field}
                            index={elementIndex}
                            fieldsLength={elements.length}
                            remove={remove}
                            move={move}
                            addField={addField}
                            register={register}
                            markers={markers}
                            isComplete={isComplete}
                            invalidTemplateIds={invalidTemplateIds}
                          />
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>
            {/* Add Block button outside the layout */}
            {group.elements.length > 0 && (
              <div className="mt-3">
                <AddBlockChooser
                  onChoose={(type) => {
                    const lastElementIndex =
                      group.elements[group.elements.length - 1].index;
                    addField(type, lastElementIndex + 1, undefined);
                  }}
                />
              </div>
            )}
          </div>
        );
      }}
    />
  );
};

const SideBarEditor: FunctionComponent<SideBarEditorProps> = ({ readOnlyOverlay, markers, invalidTemplateIds }) => {
  const { control, getValues, setValue, register } =
    useFormContext<KidDocumentForm>();

  const { fields, move, insert, remove } = useFieldArray({
    control,
    name: 'elements' as const,
  });


  const addField = useCallback(
    (type: PdfTag, index: number, layout?: LayoutConfig) => {
      const id = uuidv4();
      let newField: PdfElement;

      if (type === 'bar_chart') {
        newField = {
          id,
          fieldId: id,
          tag: type,
          content: {
            categories: ['Q1', 'Q2', 'Q3', 'Q4'],
            series: [
              {
                name: '2023',
                values: [70, 65, 80, 70],
              },
              {
                name: '2024',
                values: [85, 90, 70, 95],
              },
            ],
          },
          format_options: {
            title: 'Quarterly Performance',
            colorsList: ['#1f77b4', '#ff7f0e'],
            fontSize: 12,
            horizontal: false,
            barmode: 'group',
            showLegend: true,
            backgroundColor: 'rgba(0,0,0,0)',
            showGridX: true,
            showGridY: true,
            yaxisTitle: 'Performance (%)',
            xaxisTitle: 'Quarter',
            color: '#000000',
          },
          layout: layout,
        };
      } else if (type === 'sri_chart') {
        newField = {
          id,
          fieldId: id,
          tag: type,
          content: 1,
          layout: layout,
        };
      } else if (type === 'table') {
        newField = {
          id,
          fieldId: id,
          tag: type,
          content: createDefaultTableContent(),
          layout: layout,
        };
      } else if (type === 'divider') {
        newField = {
          id,
          fieldId: id,
          tag: type,
          content: '',
          layout: layout,
        };
      } else if (type === 'page_break') {
        newField = {
          id,
          fieldId: id,
          tag: type,
          content: '',
          layout: layout,
        };
      } else if (type === 'heading') {
        newField = {
          id,
          fieldId: id,
          tag: type,
          content: '',
          layout: layout,
        };
      } else {
        newField = {
          id,
          fieldId: id,
          tag: type,
          content: '',
          layout: layout,
        };
      }

      insert(index, newField);

      const updatedFields = getValues('elements');
      setValue('elements', updatedFields);

      return index;
    },
    [fields, getValues, insert, setValue],
  );

  return (
    <div className="relative flex h-full w-[600px] shrink-0 flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
      <div className="scrollbar-custom scrollbar-custom-inner min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
        <GroupedElementsRenderer
          fields={fields}
          remove={remove}
          move={move}
          addField={addField}
          register={register}
          markers={markers}
          invalidTemplateIds={invalidTemplateIds}
        />
      </div>
      {readOnlyOverlay}
    </div>
  );
};

export default SideBarEditor;
